class PredictiveSearch extends SearchForm {
  constructor() {
    super();

    this.ACTIVE_CLASS = "predictive-search--active";
    this.LOADING_CLASS = "predictive-search--loading";

    this.cachedResults = {};
    this.abortController = new AbortController();
    this.searchTerm = "";
  }

  /**
   * Lifecycle method called when the element is added to the DOM.
   */
  connectedCallback() {
    super.connectedCallback();

    this.addEventListeners();

    this.predictiveSearch = this.querySelector("[data-predictive-search]");
    this.predictiveSearchWrapper = this.querySelector("[data-predictive-search-wrapper]");
    this.statusElement = this.querySelector("[data-predictive-search-status]");
  }

  /**
   * Adds event listeners for form submit, input focus, and focus out.
   */
  addEventListeners() {
    this.form?.addEventListener("submit", this.onFormSubmit.bind(this));
    this.input?.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));

    subscribe(PUB_SUB_EVENTS.drawerClosed, () => this.closeResults(true));
  }

  /**
   * Handles input changes, updates search term, and fetches results.
   */
  onChange() {
    super.onChange();

    this.searchTerm = this.query;

    if (!this.searchTerm.length) return this.close();

    this.getSearchResults(this.searchTerm);
  }

  /**
   * Handles form submission, prevents submit if no query or a result is selected.
   * @param {Event} event
   */
  onFormSubmit(event) {
    if (!this.query.length) event.preventDefault();
  }

  /**
   * Handles input focus, triggers search if needed.
   */
  onFocus() {
    const currentSearchTerm = this.query;

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) this.onChange();

    this.open();
  }

  /**
   * Handles keyup events for navigation and selection.
   * @param event
   */
  onKeyup(event) {
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up")
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  /**
   * Handles focus out, closes results if focus leaves the component.
   */
  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  /**
   * Fetches search results and renders them, using cache if available.
   * @param {string} searchTerm
   */
  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();

    if (this.cachedResults[queryKey]) {
      return this.renderSearchResults(this.cachedResults[queryKey]);
    }

    this.fetchSearchResults(searchTerm, queryKey);
  }

  /**
   * Fetches search results from the server.
   * @param searchTerm
   * @param queryKey
   */
  fetchSearchResults(searchTerm, queryKey) {
    const baseUrl = routes.predictive_search_url;
    const encodedTerm = encodeURIComponent(searchTerm);
    const parameters = "&resources[options][fields]=title,product_type,variants.title,vendor,variants.sku";
    const url = `${baseUrl}?q=${encodedTerm}${parameters}&section_id=predictive-search`

    this.open();
    this.setLoadingState(true);

    fetch(url, { signal: this.abortController.signal })
      .then(response => this.handleResponse(response))
      .then(text => this.processFetchedResults(text, queryKey))
      .then(() => this.setLoadingState())
      .catch(error => this.handleError(error));
  }

  /**
   * Handles the fetch response, throwing an error for non-OK responses.
   * @param {Object} response
   * @returns Promise<string>
   */
  handleResponse(response) {
    if (!response.ok) this.handleResponseError(response);

    return response.text();
  }

  /**
   * Handles non-OK fetch responses by closing results and throwing an error.
   * @param {Object} response
   */
  handleResponseError(response) {
    this.close();

    throw new Error(response.status);
  }

  /**
   * Handles fetch errors, ignoring abort errors and closing results on others.
   * @param {Error} error
   */
  handleError(error) {
    if (error?.code === 20) return;

    this.close();

    throw error;
  }

  /**
   * Processes fetched results, caches them, and renders.
   * @param text
   * @param queryKey
   */
  processFetchedResults(text, queryKey) {
    const parsedText = new DOMParser().parseFromString(text, 'text/html')
    const resultsElement = parsedText.querySelector('#shopify-section-predictive-search');
    const resultsMarkup = resultsElement.innerHTML;

    this.cachedResults[queryKey] = resultsMarkup;
    this.renderSearchResults(resultsMarkup);
  }

  /**
   * Renders the search results markup and updates live region.
   * @param {string} resultsMarkup
   */
  renderSearchResults(resultsMarkup) {
    if (this.predictiveSearchWrapper) {
      this.predictiveSearchWrapper.innerHTML = resultsMarkup;

      this.setLiveRegionText();
      this.updateButtonState();
      this.open();
    }
  }

  /**
   * Updates the submit button state based on the search term.
   */
  updateButtonState() {
    const searchTerm = this.submitButtonText;

    if (!this.submitButton || !searchTerm) return;

    this.searchTerm.length
      ? this.submitButton.removeAttribute("disabled")
      : this.submitButton.setAttribute("disabled", "true");

    searchTerm.textContent = `"${this.searchTerm}"`;
  }

  /**
   * Switches the active option based on direction.
   * @param direction
   */
  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const searchLinks = this.querySelectorAll("[data-predictive-search-link]");

    const visibleElements = this.getVisibleElements(searchLinks);

    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    const selectedElementIndex = this.findSelectedElementIndex(visibleElements, selectedElement);

    activeElementIndex = this.setActiveElementIndex(moveUp, selectedElementIndex, visibleElements);

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = visibleElements[activeElementIndex];

    this.setActiveElement(activeElement, selectedElement);
  }

  /**
   * Filters and returns only visible elements from a NodeList.
   * @param {NodeList} elements
   * @returns {Array} Array of visible elements.
   */
  getVisibleElements(elements) {
    return Array.from(elements).filter(element => element.offsetParent !== null);
  }

  /**
   * Finds the index of the selected element within all visible elements.
   * @param {Array} allVisibleElements
   * @param {Element} selectedElement
   * @returns {number}
   */
  findSelectedElementIndex(allVisibleElements, selectedElement) {
    let selectedElementIndex = -1;
    let index = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[index] === selectedElement) selectedElementIndex = index;

      index++;
    }

    return selectedElementIndex;
  }

  /**
   * Calculates the next index in a circular manner.
   * @param {number} currentIndex
   * @param {number} length
   * @returns {number|*}
   */
  getNextIndex(currentIndex, length) {
    return currentIndex === length - 1 ? 0 : currentIndex + 1;
  }

  /**
   * Calculates the previous index in a circular manner.
   * @param {number} currentIndex
   * @param {number} length
   * @returns {number}
   */
  getPreviousIndex(currentIndex, length) {
    return currentIndex === 0 ? length - 1 : currentIndex - 1;
  }

  /**
   * Determines the new active element index based on direction.
   * @param {boolean} moveUp
   * @param {number} selectedElementIndex
   * @param {Array} visibleElements
   * @returns {number|*}
   */
  setActiveElementIndex(moveUp, selectedElementIndex, visibleElements) {
    return !moveUp && selectedElementIndex
      ? this.getNextIndex(selectedElementIndex, visibleElements.length)
      : this.getPreviousIndex(selectedElementIndex, visibleElements.length);
  }

  /**
   * Sets the active element and updates ARIA attributes.
   * @param {Element} activeElement
   * @param {Element} selectedElement
   */
  setActiveElement(activeElement, selectedElement) {
    activeElement.setAttribute('aria-selected', "true");

    if (selectedElement) selectedElement.setAttribute('aria-selected', "false");

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  /**
   * Selects the currently highlighted option.
   */
  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"][data-predictive-search-link]');

    if (selectedOption) selectedOption.click();
  }

  /**
   * Sets the loading state by toggling the loading class.
   * @param isLoading
   */
  setLoadingState(isLoading = false) {
    this.predictiveSearch.classList[isLoading ? "add" : "remove"](this.LOADING_CLASS);
  }

  /**
   * Sets the live region text for accessibility.
   */
  setLiveRegionText() {
    const countElement = this.querySelector('[data-predictive-search-live-region-count-value]');
    const statusText = countElement?.textContent;

    if (!this.statusElement) return;

    this.statusElement.textContent = statusText;
  }

  /**
   * Opens the predictive search dropdown.
   */
  open() {
    this.input?.setAttribute('aria-expanded', "true");
    this.predictiveSearch.classList.add(this.ACTIVE_CLASS);
  }

  /**
   * Closes the predictive search dropdown.
   * @param {boolean} clearSearchTerm
   */
  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
  }

  /**
   * Closes the results and optionally clears the search term.
   * @param {boolean} clearSearchTerm
   */
  closeResults(clearSearchTerm = false) {
    if (!this.input) return;
    if (clearSearchTerm) this.input.value = "";

    this.predictiveSearch.classList.remove(this.ACTIVE_CLASS);

    this.input.setAttribute('aria-activedescendant', "");
    this.input.setAttribute('aria-expanded', "false");
  }

  /**
   * Handles form reset, aborts any pending requests and closes results.
   * @param {Event} event
   */
  onFormReset(event) {
    super.onFormReset(event);

    this.searchTerm = "";
    this.abortController.abort();
    this.abortController = new AbortController();

    this.closeResults(true);
  }

  /**
   * Returns the trimmed value of the search input.
   * @returns {string}
   */
  get query() {
    return this.input?.value.trim() || "";
  }

  /**
   * Returns the submit button element.
   * @returns {Element}
   */
  get submitButton() {
    return this.querySelector("[data-search-submit]");
  }

  /**
   * Returns the submit button text element.
   * @returns {Element}
   */
  get submitButtonText() {
    return this.submitButton?.querySelector("[data-search-term]");
  }
}

customElements.define('predictive-search', PredictiveSearch);
